import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';

const MARGIN = { top: 20, right: 20, bottom: 20, left: 20 };
const HEIGHT = 480;

const styles = {
  card: {
    position: 'relative',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '4px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '12px',
  },
  summaryRow: {
    display: 'flex',
    gap: '16px',
    marginBottom: '12px',
    fontSize: '12px',
    color: '#6b7280',
  },
  summaryBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '2px 10px',
    borderRadius: '999px',
    background: '#f3f4f6',
    border: '1px solid #e5e7eb',
    color: '#374151',
    fontSize: '11px',
    fontWeight: 500,
    whiteSpace: 'nowrap',
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
    marginBottom: '12px',
    alignItems: 'center',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
    fontWeight: 500,
    color: '#374151',
  },
  legendDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  legendCount: {
    fontSize: '11px',
    color: '#9ca3af',
    fontWeight: 400,
  },
  container: {
    overflowX: 'auto',
    minWidth: '400px',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    maxWidth: '240px',
  },
  emptyState: {
    color: '#6b7280',
    textAlign: 'center',
    padding: '40px 0',
  },
};

/**
 * Compute the convex hull for a set of 2D points.
 * Returns an array of [x, y] pairs forming the hull polygon, or null if fewer than 3 points.
 */
function computeConvexHull(points, xScale, yScale) {
  if (!points || points.length < 3) return null;
  const mapped = points.map((p) => [xScale(p.x), yScale(p.y)]);
  return d3.polygonHull(mapped);
}

export default function TSNEClusterMap({ data }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  useEffect(() => {
    if (!data || !data.points || data.points.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const containerWidth = containerRef.current
      ? containerRef.current.clientWidth
      : 700;
    const width = Math.max(400, containerWidth);
    const innerW = width - MARGIN.left - MARGIN.right;
    const innerH = HEIGHT - MARGIN.top - MARGIN.bottom;

    svg.attr('width', width).attr('height', HEIGHT);

    const g = svg.append('g').attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`);

    const { points, clusters } = data;

    // Build cluster lookup
    const clusterMap = {};
    if (clusters) {
      clusters.forEach((c) => {
        clusterMap[c.id] = c;
      });
    }

    // Scales -- t-SNE coordinates are arbitrary, no axis labels needed
    const xExtent = d3.extent(points, (d) => d.x);
    const yExtent = d3.extent(points, (d) => d.y);
    const xPad = (xExtent[1] - xExtent[0]) * 0.1 || 5;
    const yPad = (yExtent[1] - yExtent[0]) * 0.1 || 5;

    const x = d3.scaleLinear()
      .domain([xExtent[0] - xPad, xExtent[1] + xPad])
      .range([0, innerW]);

    const y = d3.scaleLinear()
      .domain([yExtent[0] - yPad, yExtent[1] + yPad])
      .range([innerH, 0]);

    // Light grid background
    g.append('rect')
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', innerW)
      .attr('height', innerH)
      .attr('fill', '#fafbfc')
      .attr('rx', 4);

    // Draw convex hulls for each cluster
    if (clusters && clusters.length > 0) {
      clusters.forEach((cluster) => {
        const clusterPoints = points.filter((p) => p.cluster === cluster.id);
        const hull = computeConvexHull(clusterPoints, x, y);

        if (hull) {
          // Draw filled hull polygon
          const hullPath = d3.line()
            .x((d) => d[0])
            .y((d) => d[1])
            .curve(d3.curveCatmullRomClosed);

          g.append('path')
            .attr('d', hullPath(hull))
            .attr('fill', cluster.color || '#6b7280')
            .attr('fill-opacity', 0.08)
            .attr('stroke', cluster.color || '#6b7280')
            .attr('stroke-opacity', 0.25)
            .attr('stroke-width', 1.5)
            .attr('stroke-dasharray', '4,3');
        } else if (clusterPoints.length === 2) {
          // For 2 points, draw a connecting line with a light stroke
          g.append('line')
            .attr('x1', x(clusterPoints[0].x))
            .attr('y1', y(clusterPoints[0].y))
            .attr('x2', x(clusterPoints[1].x))
            .attr('y2', y(clusterPoints[1].y))
            .attr('stroke', cluster.color || '#6b7280')
            .attr('stroke-opacity', 0.2)
            .attr('stroke-width', 2)
            .attr('stroke-dasharray', '4,3');
        }

        // Cluster label at centroid
        if (cluster.centroid) {
          g.append('text')
            .attr('x', x(cluster.centroid.x))
            .attr('y', y(cluster.centroid.y) - 14)
            .attr('text-anchor', 'middle')
            .attr('font-size', '11px')
            .attr('font-weight', 600)
            .attr('fill', cluster.color || '#6b7280')
            .attr('opacity', 0.7)
            .attr('font-family', "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif")
            .text(cluster.label || `Cluster ${cluster.id}`);
        }
      });
    }

    // Draw points
    points.forEach((d) => {
      const cluster = clusterMap[d.cluster];
      const color = cluster ? (cluster.color || '#6b7280') : '#6b7280';

      g.append('circle')
        .attr('cx', x(d.x))
        .attr('cy', y(d.y))
        .attr('r', 5)
        .attr('fill', color)
        .attr('opacity', 0.75)
        .attr('stroke', '#fff')
        .attr('stroke-width', 1)
        .style('cursor', 'pointer')
        .on('mousemove', (event) => {
          d3.select(event.currentTarget)
            .attr('r', 8)
            .attr('opacity', 1)
            .attr('stroke-width', 2);
          setTooltip({
            x: event.clientX + 12,
            y: event.clientY - 10,
            item: d,
            clusterLabel: cluster ? (cluster.label || `Cluster ${cluster.id}`) : 'Unknown',
            color,
          });
        })
        .on('mouseleave', (event) => {
          d3.select(event.currentTarget)
            .attr('r', 5)
            .attr('opacity', 0.75)
            .attr('stroke-width', 1);
          setTooltip(null);
        });
    });

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data]);

  // Empty state
  if (!data || !data.points || data.points.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.title}>Address Clustering (t-SNE)</div>
        <div style={styles.subtitle}>Nonlinear similarity mapping of pricing behavior</div>
        <div style={styles.emptyState}>No cluster data available</div>
      </div>
    );
  }

  const { clusters, perplexity, numClusters } = data;

  return (
    <div style={styles.card}>
      <div style={styles.title}>Address Clustering (t-SNE)</div>
      <div style={styles.subtitle}>Nonlinear similarity mapping of pricing behavior</div>

      {/* Summary badges */}
      <div style={styles.summaryRow}>
        {perplexity != null && (
          <span style={styles.summaryBadge}>Perplexity: {perplexity}</span>
        )}
        {numClusters != null && (
          <span style={styles.summaryBadge}>
            {numClusters} cluster{numClusters !== 1 ? 's' : ''}
          </span>
        )}
        <span style={styles.summaryBadge}>
          {data.points.length} address{data.points.length !== 1 ? 'es' : ''}
        </span>
      </div>

      {/* Legend */}
      {clusters && clusters.length > 0 && (
        <div style={styles.legend}>
          {clusters.map((cluster) => (
            <div key={cluster.id} style={styles.legendItem}>
              <span
                style={{
                  ...styles.legendDot,
                  background: cluster.color || '#6b7280',
                }}
              />
              <span>{cluster.label || `Cluster ${cluster.id}`}</span>
              <span style={styles.legendCount}>({cluster.count})</span>
            </div>
          ))}
        </div>
      )}

      <div style={styles.container} ref={containerRef}>
        <svg ref={svgRef} />
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            {tooltip.item.label}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>State</span>
            <span style={{ fontWeight: 600 }}>{tooltip.item.state}</span>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              marginTop: '4px',
              paddingTop: '4px',
              borderTop: '1px solid #f3f4f6',
            }}
          >
            <span
              style={{
                ...styles.legendDot,
                background: tooltip.color,
                width: '8px',
                height: '8px',
              }}
            />
            <span style={{ color: '#6b7280' }}>{tooltip.clusterLabel}</span>
          </div>
        </div>
      )}
    </div>
  );
}
